import { useState, useMemo } from "react";
import { Form, Formik } from "formik";
import { useParams } from "react-router-dom";
import { Drawer, Stack, Typography, IconButton, Divider } from "@mui/material";
import { Menu as IMenu } from "@/types/menus";
import useSnackbar from "@/hooks/useSnackbar";
import ShareLinks from "@/components/ShareLinks";
import MenuDetailsTab, { MenuDetailsSchema } from "./MenuDetailsTab";
import UpdateMenuActions from "./UpdateMenuActions";
import { useUpdateMenuMutation } from "@/features/organizations/api/menusApi";
import { useAppSelector } from "@/hooks";
import { Cancel01Icon, LinkSquare02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";

interface Props {
  selectedMenu?: IMenu;
  onClose: () => void;
}

const UpdateMenuDrawer = (props: Props) => {
  const { selectedMenu, onClose } = props;
  const [openSnackbar] = useSnackbar();
  const [updateMenu, updateMenuMutation] = useUpdateMenuMutation();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const currentOrganization = useAppSelector((store) => store.organizations[organizationId] ?? null);

  const isNewContent = useMemo(() => !selectedMenu?.id, [selectedMenu]);

  const handleSubmit = async (values: IMenu) => {
    const response = await updateMenu({ menu: values, organizationId });

    if (response?.error) {
      openSnackbar("Error al actualizar el menú", "error");
    } else {
      openSnackbar("Menú actualizado", "success");
    }
  };

  return (
    <Drawer open={Boolean(selectedMenu && selectedMenu)} onClose={onClose}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          padding: 2,
          paddingBottom: 0,
        }}
      >
        <Typography variant="h2">{isNewContent ? "Crear" : "Editar"} menú</Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton color="primary" size="large" onClick={() => setQrDialogOpen(true)} sx={{ backgroundColor: "#ffffff" }}>
            <HugeiconsIcon icon={LinkSquare02Icon} />
          </IconButton>
          <IconButton color="primary" size="large" onClick={onClose} sx={{ backgroundColor: "#ffffff" }}>
            <HugeiconsIcon icon={Cancel01Icon} />
          </IconButton>
        </Stack>
      </Stack>

      <Divider sx={{ mt: 2 }} />

      <Stack padding={2} gap={2} flex={1}>
        <Formik initialValues={{ ...selectedMenu }} onSubmit={handleSubmit} validationSchema={MenuDetailsSchema}>
          {() => (
            <Form>
              <Stack gap={2}>
                <MenuDetailsTab />
                <UpdateMenuActions {...props} isLoading={updateMenuMutation.isLoading} />
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>

      <ShareLinks alias={currentOrganization?.alias ?? ""} menuId={selectedMenu?.id ?? ""} open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} title="Descargar" />
    </Drawer>
  );
};

export default UpdateMenuDrawer;
